import { Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  BarChart3,
  Copy,
  ExternalLink,
  LayoutDashboard,
  Loader2,
  RefreshCw,
  Shield,
  UserCircle,
  UserPlus,
  Users,
} from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { formatDateTimeEs, formatShortDay } from "@/lib/format-date";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { PlatformAdminShell } from "./PlatformAdminShell";
import { ADMIN_HUBS, ADMIN_PAGE_TITLES, ADMIN_ROOT_HUB, ADMIN_TAB_IDS, DEMO_LABELS, PLAN_PRICE_USD, isAdminHubTab, isAdminTabId, type AdminTabId } from "./admin-nav";
import { AdminDomainHub, AdminHubBack } from "./AdminDomainHub";
import { AdminStatCard as StatCard } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  apiGetJson,
  apiSendJson,
  clientRequestHeaders,
} from "@/lib/sm-api";
import { DEMO_SLUGS, DEMO_TENANT_IDS, type DemoSlug } from "@/lib/demo-registry";
import { AdminFlagsPanel } from "./AdminFlagsPanel";
import { AdminAnalyticsMetricsPanel } from "./AdminAnalyticsMetricsPanel";
import { AdminCronRegistryPanel } from "./AdminCronRegistryPanel";
import { AdminPlansCatalogPanel } from "./AdminPlansCatalogPanel";
import { AdminRolesPanel } from "./AdminRolesPanel";
import { AdminPlatformEmailPanel } from "./AdminPlatformEmailPanel";
import { PlatformStaffPanel } from "./PlatformStaffPanel";
import { PlatformStatusPanel } from "./PlatformStatusPanel";
import { PaywayAdminPanel } from "./PaywayAdminPanel";
import { SaasMpBillingPanel } from "./SaasMpBillingPanel";
import { SaasBillingConfigPanel } from "./SaasBillingConfigPanel";
import { NetlifyEnvCenterPanel } from "./NetlifyEnvCenterPanel";
import { BillingAdminPanel } from "./BillingAdminPanel";
import { FeatureCatalogPanel } from "./FeatureCatalogPanel";
import { HelpAnalyticsPanel } from "./HelpAnalyticsPanel";
import { PlatformUsageOpsPanel } from "./PlatformUsageOpsPanel";
import { SupportInboxPanel } from "./SupportInboxPanel";
import { AdminPacksPanel } from "./AdminPacksPanel";
import { PushTestingPanel } from "./PushTestingPanel";
import { AdminReleasesPanel } from "./AdminReleasesPanel";
import { OnboardingSessionsPanel } from "./OnboardingSessionsPanel";
import { AdminApprovalsPanel } from "./AdminApprovalsPanel";
import { PlatformComplianceRequestsPanel } from "./PlatformComplianceRequestsPanel";
import { LeadsPanel } from "./LeadsPanel";
import { CobrosOpsPanel } from "./CobrosOpsPanel";
import { MessagingAdminPanel } from "./MessagingAdminPanel";
import { AdminEmailTemplatesPanel } from "./AdminEmailTemplatesPanel";
import { AdminEmailGatesPanel } from "./AdminEmailGatesPanel";
import { CapabilityNichesMatrixPanel } from "./CapabilityNichesMatrixPanel";
import { ProfileNavMatrixPanel } from "./ProfileNavMatrixPanel";
import { PageLoader } from "@/components/PageLoader";
import { LeadDetailDialog } from "./LeadDetailDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WorkspaceActionsMenu } from "./WorkspaceActionsMenu";
import { CreateDemoLeadDialog } from "./CreateDemoLeadDialog";
import { TenantLimitsOverrideDialog } from "./TenantLimitsOverrideDialog";
import { STAFF_PERMISSION_ROWS, STAFF_PERMISSION_DEFAULTS } from "@/lib/staff-permissions";
import { cn } from "@/lib/utils";
import { BUSINESS_PROFILE_LABELS, type BusinessProfile } from "@/lib/business-profile";
import { getTenantBaseDomain } from "@/lib/tenant-dns";
import { useAppDialogs } from "@/components/ui/app-dialogs";

type Stats = {
  tenants: number;
  users: number;
  leadsPending: number;
  approvalsPending?: number;
  ordersTotal: number;
  ordersToday: number;
  productsTotal: number;
  activeTenants: number;
  demoTenants: number;
  byTenant: Record<string, number>;
  ordersTodayByTenant: Record<string, number>;
  planBreakdown: Record<string, number>;
  leadsByStatus: Record<string, number>;
  ordersPerDay30: { date: string; count: number }[];
  ordersByHour: { hour: number; count: number }[];
  churn30: number;
};

type TenantRow = {
  id: string;
  name: string;
  slug: string;
  subdomain?: string | null;
  email: string;
  lifecycleStatus?: string;
  trialEndsAt?: string | null;
  planId: string | null;
  subStatus: string | null;
  validUntil: string | null;
  productFlags?: Record<string, boolean>;
  customDomain?: string | null;
  createdAt?: string;
  businessProfile?: string | null;
  onboardingDrafts?: {
    demoLead?: {
      businessName: string;
      niche: string;
      contactName: string | null;
      contactEmail: string | null;
      contactPhone: string | null;
      referenceUrl: string | null;
      createdBy: string;
      createdAt: string;
    };
  };
};

type LeadRow = {
  id: string;
  nombre: string;
  negocio: string;
  email: string;
  telefono: string;
  plan_interesado: string;
  status: string;
  created_at: string;
  form_data?: Record<string, unknown> | null;
};

type AppUserRow = {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string | null;
  active: boolean;
  createdAt?: string | null;
  lastAccessAt?: string | null;
};

type InvitationRow = {
  id: string;
  email: string;
  business_name: string;
  trial_days: number;
  token?: string;
  created_at: string;
  accepted_at: string | null;
  status?: string;
  expires_at?: string | null;
};

const NO_TENANT_SELECT = "__none__";
const USERS_PAGE_SIZE = 50;

type WorkspaceKind = "client" | "demo_seed" | "demo_lead";

/**
 * SCRUM-294: is_demo era un booleano único que mezclaba demos con seed propias
 * (DEMO_SLUGS) y demos generadas para un lead concreto (SCRUM-295, todavía sin
 * mecanismo de creación). is_demo se mantiene derivado — nada que ya lo lea
 * (gates, planes, límites) cambia de comportamiento.
 */
function getWorkspaceKind(productFlags?: Record<string, boolean>): WorkspaceKind {
  if (productFlags?.is_demo_lead) return "demo_lead";
  if (productFlags?.is_demo_seed || productFlags?.is_demo) return "demo_seed";
  return "client";
}

const WORKSPACE_KIND_LABELS: Record<WorkspaceKind, string> = {
  client: "Cliente",
  demo_seed: "Demo con seed",
  demo_lead: "Demo por lead",
};

/**
 * Planes canónicos para asignar a un workspace.
 * NO usar nombres de marketing de sm_plans.name ("Empezá a vender", etc.).
 * IDs + labels de producto: free_trial | starter | PRO | business | enterprise.
 */
const CANONICAL_WORKSPACE_PLAN_IDS = [
  "free_trial",
  "starter",
  "pro",
  "business",
  "enterprise",
] as const;

const PLAN_LABELS_CANONICAL: Record<(typeof CANONICAL_WORKSPACE_PLAN_IDS)[number], string> = {
  free_trial: "free_trial",
  starter: "starter",
  pro: "PRO",
  business: "business",
  enterprise: "enterprise",
};

type CatalogPlanOption = { id: string; name: string; sortOrder: number; isPublic: boolean };

function lifecycleBadge(status?: string) {
  const s = status ?? "active";
  if (s === "active")
    return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30">Activo</Badge>;
  if (s === "paused") return <Badge variant="secondary">Pausado</Badge>;
  if (s === "trial") return <Badge variant="outline">Trial</Badge>;
  return <Badge variant="outline">{s}</Badge>;
}

export function PlatformAdminPage({
  userEmail,
  onLogout,
  initialTab,
}: {
  userEmail?: string;
  onLogout: () => void;
  initialTab?: AdminTabId;
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<AdminTabId>(initialTab && isAdminTabId(initialTab) ? initialTab : "dashboard");
  const { confirm, prompt } = useAppDialogs();
  const [dolarBlue, setDolarBlue] = useState<{ mid: number } | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [leadDetailId, setLeadDetailId] = useState<string | null>(null);
  const [users, setUsers] = useState<AppUserRow[]>([]);
  const [invitations, setInvitations] = useState<InvitationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [invEmail, setInvEmail] = useState("");
  const [invBusiness, setInvBusiness] = useState("");
  const [invDays, setInvDays] = useState("14");
  const [invNotes, setInvNotes] = useState("");
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteEmailSent, setInviteEmailSent] = useState<boolean | null>(null);
  const [inviteSent, setInviteSent] = useState<string | null>(null);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [nuEmail, setNuEmail] = useState("");
  const [nuName, setNuName] = useState("");
  const [nuRole, setNuRole] = useState<"owner" | "staff">("owner");
  const [nuTenantId, setNuTenantId] = useState("");
  const [nuSaving, setNuSaving] = useState(false);
  const [pwUser, setPwUser] = useState<AppUserRow | null>(null);
  const [pwTemp, setPwTemp] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<AppUserRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editWorkspaceId, setEditWorkspaceId] = useState(NO_TENANT_SELECT);
  const [tenantSearch, setTenantSearch] = useState("");
  const [tenantProfileFilter, setTenantProfileFilter] = useState<string>("all");
  const [tenantPlanFilter, setTenantPlanFilter] = useState<string>("all");
  const [tenantStatusFilter, setTenantStatusFilter] = useState<string>("all");
  const [tenantDemoFilter, setTenantDemoFilter] = useState<string>("all");
  const [createDemoLeadOpen, setCreateDemoLeadOpen] = useState(false);
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userTenantFilter, setUserTenantFilter] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [usersHasMore, setUsersHasMore] = useState(false);
  const [usersLoadingMore, setUsersLoadingMore] = useState(false);
  const [deleteTenantId, setDeleteTenantId] = useState<string | null>(null);
  const [deleteConfirmSlug, setDeleteConfirmSlug] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [limitsTenant, setLimitsTenant] = useState<{ id: string; name: string } | null>(null);
  const [sandboxTab, setSandboxTab] = useState<"demos" | "clientes" | "seeds" | "utilities">("demos");
  const [demoBusy, setDemoBusy] = useState<string | null>(null);
  const [staffPermState, setStaffPermState] = useState(STAFF_PERMISSION_DEFAULTS);
  const [catalogPlans, setCatalogPlans] = useState<CatalogPlanOption[]>([]);

  const authHeaders = useCallback(() => clientRequestHeaders(), []);

  const setTabAndUrl = useCallback((id: AdminTabId) => {
    setTab(id);
    void navigate({ to: "/admin", search: { tab: id } });
  }, [navigate]);

  useEffect(() => {
    if (initialTab && isAdminTabId(initialTab) && initialTab !== tab) {
      setTab(initialTab);
    }
  }, [initialTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const planLabel = useCallback((planId: string | null | undefined) => {
    const id = (planId ?? "starter").toLowerCase();
    if (id in PLAN_LABELS_CANONICAL) {
      return PLAN_LABELS_CANONICAL[id as keyof typeof PLAN_LABELS_CANONICAL];
    }
    return id;
  }, []);

  const planSelectOptions = useMemo(() => {
    const byId = new Map(catalogPlans.map((p) => [p.id, p]));
    return CANONICAL_WORKSPACE_PLAN_IDS.map((id, idx) => {
      const fromDb = byId.get(id);
      return {
        id,
        name: PLAN_LABELS_CANONICAL[id],
        sortOrder: fromDb?.sortOrder ?? idx * 10,
        isPublic: fromDb?.isPublic ?? id !== "free_trial",
      };
    });
  }, [catalogPlans]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      apiGetJson<Stats>("/api/admin/stats"),
      apiGetJson<TenantRow[]>("/api/admin/tenants"),
      apiGetJson<LeadRow[]>("/api/leads"),
      apiGetJson<AppUserRow[]>(`/api/admin/users?excludePlatformStaff=1&limit=${USERS_PAGE_SIZE}&offset=0`),
      apiGetJson<InvitationRow[]>("/api/invitations"),
      apiGetJson<{ permissions: typeof STAFF_PERMISSION_DEFAULTS }>("/api/admin/staff-permissions"),
      apiGetJson<{ plans: CatalogPlanOption[] }>("/api/admin/plans"),
    ]);
    const labels = ["stats", "tenants", "leads", "users", "invitations", "permisos", "planes"];
    results.forEach((r, i) => {
      if (r.status === "rejected")
        toast.error(`${labels[i]}: ${r.reason instanceof Error ? r.reason.message : "Error"}`);
    });
    if (results[0].status === "fulfilled") setStats(results[0].value);
    if (results[1].status === "fulfilled") setTenants(results[1].value);
    if (results[2].status === "fulfilled") setLeads(results[2].value);
    if (results[3].status === "fulfilled") {
      setUsers(results[3].value);
      setUsersHasMore(results[3].value.length === USERS_PAGE_SIZE);
    }
    if (results[4].status === "fulfilled") setInvitations(results[4].value);
    if (results[5].status === "fulfilled") setStaffPermState(results[5].value.permissions);
    if (results[6].status === "fulfilled") {
      setCatalogPlans(
        (results[6].value.plans ?? []).map((p) => ({
          id: p.id,
          name: p.name,
          sortOrder: Number(p.sortOrder ?? 0),
          isPublic: p.isPublic !== false,
        })),
      );
    }
    setLoading(false);
  }, []);

  const provisionTenantSubdomain = useCallback(
    async (tenantId: string, slug: string, currentSubdomain?: string | null) => {
      // Input editable con el subdominio actual o el slug como valor por defecto.
      const zone = getTenantBaseDomain();
      const input = await prompt({
        title: "Subdominio a crear",
        description: `Se publicará como {subdominio}.${zone}`,
        defaultValue: (currentSubdomain || slug).trim(),
        confirmLabel: "Crear",
      });
      if (input === null) return; // cancelado
      const sub = input.trim().toLowerCase();
      if (!sub) {
        toast.error("El subdominio no puede estar vacío");
        return;
      }
      try {
        const res = await apiSendJson<{
          subdomain?: string;
          subdomainProvision?: { status: string; detail?: string };
        }>("/api/admin/tenants", "PATCH", { tenantId, provisionSubdomain: true, subdomain: sub });
        const p = res.subdomainProvision;
        const finalSub = res.subdomain ?? sub;
        if (p?.status === "active") {
          const fqdn = p.detail?.includes(".") ? p.detail : `${finalSub}.${getTenantBaseDomain()}`;
          toast.success(`Subdominio creado: ${fqdn}`);
          void loadAll();
        } else if (p?.status === "skipped") {
          const reason =
            p.detail === "netlify_not_configured"
              ? "Falta configurar NETLIFY_API_TOKEN / NETLIFY_DNS_ZONE_ID en Netlify"
              : p.detail === "no_zone_id"
                ? "Falta NETLIFY_DNS_ZONE_ID en Netlify"
                : (p.detail ?? "config Netlify ausente");
          toast.warning(`No se pudo provisionar: ${reason}`);
        } else toast.error(`Falló: ${p?.detail ?? "error"}`);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Error al provisionar subdominio");
      }
    },
    [loadAll],
  );


  const setCustomDomain = useCallback(
    async (tenantId: string, current?: string | null) => {
      const input = await prompt({
        title: "Dominio propio del tenant",
        description: "Vacío para quitar. Ej: pedidos.minegocio.com",
        defaultValue: current ?? "",
        allowEmpty: true,
        confirmLabel: "Guardar",
      });
      if (input === null) return;
      try {
        await apiSendJson("/api/admin/tenants", "PATCH", { tenantId, customDomain: input.trim() || null });
        toast.success(
          input.trim()
            ? "Dominio asignado. Configurá el DNS del cliente apuntando a la app."
            : "Dominio quitado",
        );
        void loadAll();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : "Error al asignar dominio");
      }
    },
    [loadAll],
  );

  const saveStaffPermissions = useCallback(async () => {
    try {
      await apiSendJson("/api/admin/staff-permissions", "PATCH", { permissions: staffPermState });
      toast.success("Permisos de staff guardados");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar permisos");
    }
  }, [staffPermState]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    fetch("https://dolarapi.com/v1/dolares/blue")
      .then((r) => (r.ok ? (r.json() as Promise<{ compra: number; venta: number }>) : null))
      .then((d) => {
        if (d) setDolarBlue({ mid: (d.compra + d.venta) / 2 });
      })
      .catch(() => {});
  }, []);

  const impersonate = async (tenantId: string) => {
    const prompted = await prompt({
      title: "Motivo de impersonación",
      description: "Mínimo 10 caracteres.",
      defaultValue: `Soporte admin — impersonación tenant ${tenantId.slice(0, 8)}`,
      confirmLabel: "Impersonar",
    });
    if (prompted === null) return;
    const reason = prompted.trim() || `Soporte admin — impersonación tenant ${tenantId}`;
    if (reason.length < 10) {
      toast.error("El motivo debe tener al menos 10 caracteres");
      return;
    }
    // El admin entra al backoffice nuevo: este servidor firma un enlace de un solo uso (60 s) y el backoffice lo canjea.
    void apiSendJson<{ backofficeUrl: string }>("/api/admin/impersonate", "POST", { tenantId, reason, target: "backoffice" })
      .then((r) => {
        // Sin la opción "noopener": con ella window.open devuelve siempre null y el respaldo abría el enlace una segunda vez,
        // consumiendo el enlace de un solo uso en dos pestañas ("Este enlace ya se usó").
        const win = window.open(r.backofficeUrl, "_blank");
        if (win) win.opener = null;
        else window.location.assign(r.backofficeUrl);
      })
      .catch((e) => {
        toast.error(e instanceof Error ? e.message : "Error al impersonar tenant");
      });
  };

  const patchPlan = async (tenantId: string, planId: string) => {
    const until = new Date();
    until.setFullYear(until.getFullYear() + 1);
    try {
      await apiSendJson("/api/admin/tenants", "PATCH", {
        tenantId,
        planId,
        validUntil: until.toISOString().slice(0, 10),
        status: "active",
      });
      toast.success("Plan actualizado");
      void loadAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const patchBusinessProfile = async (tenantId: string, businessProfile: string) => {
    try {
      await apiSendJson("/api/admin/tenants", "PATCH", {
        tenantId,
        businessProfile,
        applyFeaturePreset: true,
      });
      toast.success("Business Profile actualizado");
      void loadAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };


  const sendInvitation = async () => {
    if (!invEmail.trim() || !invBusiness.trim()) {
      toast.error("Email y nombre del negocio requeridos");
      return;
    }
    setInviteSending(true);
    setInviteSent(null);
    setInviteEmailSent(null);
    try {
      const res = await apiSendJson<{ acceptUrl?: string; emailSent?: boolean }>("/api/invitations", "POST", {
        email: invEmail.trim(),
        businessName: invBusiness.trim(),
        trialDays: Number(invDays) || 14,
        invitedBy: "Structura Market",
        notes: invNotes.trim() || undefined,
      });
      setInviteSent(res.acceptUrl ?? "");
      setInviteEmailSent(Boolean(res.emailSent));
      setInvEmail("");
      setInvBusiness("");
      setInvNotes("");
      toast.success(
        res.emailSent
          ? "Invitación creada y email enviado"
          : "Invitación creada — compartí el link (email no enviado: Resend no configurado)",
      );
      void loadAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setInviteSending(false);
    }
  };

  const patchAdminUser = async (body: {
    userId: string;
    name?: string;
    email?: string;
    role?: "owner" | "staff";
    tenantId?: string | null;
    active?: boolean;
  }) => {
    try {
      await apiSendJson("/api/admin/users", "PATCH", body);
      return { ok: true as const };
    } catch (e) {
      return { error: e instanceof Error ? e.message : "Error" };
    }
  };

  const openUserEditor = (u: AppUserRow) => {
    setEditUser(u);
    setEditName(u.name);
    setEditEmail(u.email);
    setEditWorkspaceId(u.tenantId ?? NO_TENANT_SELECT);
  };

  const editWorkspaceOptions = useMemo(() => {
    if (!editUser?.tenantId || tenants.some((t) => t.id === editUser.tenantId)) return tenants;
    return [
      ...tenants,
      {
        id: editUser.tenantId,
        name: `Workspace (${editUser.tenantId.slice(0, 8)}…)`,
        slug: "",
        email: "",
        planId: null,
        subStatus: null,
        validUntil: null,
      } as TenantRow,
    ];
  }, [tenants, editUser]);

  const editWorkspaceSelectValue =
    editWorkspaceId === NO_TENANT_SELECT
      ? NO_TENANT_SELECT
      : tenants.some((t) => t.id === editWorkspaceId) || editWorkspaceId === editUser?.tenantId
        ? editWorkspaceId
        : NO_TENANT_SELECT;

  const saveEditedUser = async () => {
    if (!editUser) return;
    const ws =
      editUser.role === "super_admin"
        ? undefined
        : editWorkspaceSelectValue === NO_TENANT_SELECT
          ? null
          : editWorkspaceSelectValue;
    const r = await patchAdminUser({
      userId: editUser.id,
      name: editName.trim(),
      email: editEmail.trim(),
      ...(ws !== undefined ? { tenantId: ws } : {}),
    });
    if ("error" in r) {
      toast.error(r.error);
      return;
    }
    toast.success("Usuario actualizado");
    setEditUser(null);
    void loadAll();
  };

  const buildUsersParams = useCallback(
    (offset: number) => {
      const params = new URLSearchParams();
      if (userTenantFilter !== "all") params.set("tenantId", userTenantFilter);
      if (userRoleFilter !== "all") params.set("role", userRoleFilter);
      else params.set("excludePlatformStaff", "1");
      if (userSearch.trim()) params.set("q", userSearch.trim());
      params.set("limit", String(USERS_PAGE_SIZE));
      params.set("offset", String(offset));
      return params;
    },
    [userTenantFilter, userRoleFilter, userSearch],
  );

  // SCRUM-296: filtros y búsqueda de Usuarios van server-side (WHERE en la query,
  // no en memoria) — este efecto es independiente de loadAll() para no refetchear
  // stats/tenants/leads/etc. en cada tecla del buscador.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await apiGetJson<AppUserRow[]>(`/api/admin/users?${buildUsersParams(0)}`);
        if (cancelled) return;
        setUsers(data);
        setUsersHasMore(data.length === USERS_PAGE_SIZE);
      } catch (e) {
        if (!cancelled) toast.error(e instanceof Error ? e.message : "Error al cargar usuarios");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [buildUsersParams]);

  const loadMoreUsers = async () => {
    setUsersLoadingMore(true);
    try {
      const data = await apiGetJson<AppUserRow[]>(`/api/admin/users?${buildUsersParams(users.length)}`);
      setUsers((prev) => [...prev, ...data]);
      setUsersHasMore(data.length === USERS_PAGE_SIZE);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar más usuarios");
    } finally {
      setUsersLoadingMore(false);
    }
  };

  const createAdminUser = async (courtesyOverride?: boolean, courtesyReason?: string) => {
    if (!nuEmail.trim() || !nuTenantId) {
      toast.error("Email y workspace son obligatorios");
      return;
    }
    setNuSaving(true);
    try {
      const res = await apiSendJson<{ tempPassword: string; email: string }>("/api/admin/users", "POST", {
        email: nuEmail.trim(),
        name: nuName.trim() || nuEmail.split("@")[0],
        tenantId: nuTenantId,
        role: nuRole,
        ...(courtesyOverride ? { courtesyOverride: true, courtesyReason } : {}),
      });
      setPwUser({ id: "", email: res.email, name: nuName, role: nuRole, tenantId: nuTenantId, active: true });
      setPwTemp(res.tempPassword);
      setNewUserOpen(false);
      setNuEmail("");
      setNuName("");
      toast.success("Usuario creado");
      void loadAll();
    } catch (e) {
      const body = (e as { body?: { requiresCourtesyConfirm?: boolean; limit?: number; current?: number } } | undefined)?.body;
      if (body?.requiresCourtesyConfirm) {
        setNuSaving(false);
        const ok = await confirm({
          title: "Workspace en el límite de su plan",
          description: `Este workspace ya tiene ${body.current}/${body.limit} miembros del equipo permitidos por su plan. ¿Confirmás agregar este usuario igual, como cortesía? Queda registrado en el log de auditoría.`,
          confirmLabel: "Agregar como cortesía",
        });
        if (!ok) return;
        const reason = await prompt({
          title: "Motivo de la cortesía",
          description: "Se guarda en el log de auditoría junto con el alta.",
          placeholder: "Ej: cliente estratégico, pidió un asiento extra temporal…",
        });
        if (reason == null) return;
        await createAdminUser(true, reason);
        return;
      }
      toast.error(e instanceof Error ? e.message : "Error al crear usuario");
    } finally {
      setNuSaving(false);
    }
  };

  const setLifecycle = async (
    tenantId: string,
    lifecycleStatus: "active" | "paused" | "suspended" | "pending_approval",
  ) => {
    try {
      await apiSendJson("/api/admin/tenants", "PATCH", { tenantId, lifecycleStatus });
      const msg =
        lifecycleStatus === "paused"
          ? "Workspace pausado"
          : lifecycleStatus === "suspended"
            ? "Workspace suspendido"
            : "Workspace activado";
      toast.success(msg);
      void loadAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const patchSubscription = async (tenantId: string, status: "active" | "suspended") => {
    try {
      await apiSendJson("/api/admin/tenants", "PATCH", { tenantId, status });
      toast.success(status === "active" ? "Plan reactivado" : "Plan suspendido");
      void loadAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const runDemoSeed = async (slug: DemoSlug, opts?: { refreshImagesOnly?: boolean }) => {
    setDemoBusy(`${slug}-seed`);
    try {
      await apiSendJson<Record<string, unknown>>("/api/admin/demo-seed", "POST", {
        demo: slug,
        refreshImagesOnly: opts?.refreshImagesOnly ?? false,
      });
      toast.success(`Demo actualizado: ${DEMO_LABELS[slug] ?? slug}`);
      void loadAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error en seed");
    } finally {
      setDemoBusy(null);
    }
  };

  const runDemoReset = async (slug: DemoSlug, tenantId: string) => {
    setDemoBusy(`${tenantId}-reset`);
    try {
      const res = await apiSendJson<{ deleted?: { orders: number } }>("/api/admin/demo-reset", "POST", {
        tenantId,
        demo: slug,
      });
      toast.success(`Pedidos demo borrados: ${res.deleted?.orders ?? 0}`);
      void loadAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error en reset");
    } finally {
      setDemoBusy(null);
    }
  };

  const deleteTenant = async (tenantId: string) => {
    const target = tenants.find((t) => t.id === tenantId);
    if (!target) {
      toast.error("Tenant no encontrado");
      return;
    }
    if (deleteConfirmSlug.trim() !== target.slug) {
      toast.error(`Escribí el slug exacto «${target.slug}» para confirmar`);
      return;
    }
    setDeleteBusy(true);
    try {
      const resp = await fetch(`/api/admin/tenants?id=${encodeURIComponent(tenantId)}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const body = (await resp.json().catch(() => ({}))) as {
        error?: string;
        r2?: { failed?: number; skipped?: boolean; deleted?: number };
      };
      if (!resp.ok) throw new Error(body.error || "No se pudo eliminar");
      if (body.r2 && body.r2.failed && body.r2.failed > 0) {
        toast.warning(
          `Workspace eliminado en DB, pero R2 reportó ${body.r2.failed} fallos (revisar logs).`,
        );
      } else if (body.r2?.skipped) {
        toast.success("Workspace eliminado (R2 omitido: sin credenciales)");
      } else {
        toast.success("Workspace eliminado (DB + R2)");
      }
      setDeleteTenantId(null);
      setDeleteConfirmSlug("");
      void loadAll();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setDeleteBusy(false);
    }
  };

    const filteredTenants = useMemo(() => {
    const q = tenantSearch.trim().toLowerCase();
    return tenants.filter((t) => {
      if (q) {
        const hit =
          t.name.toLowerCase().includes(q)
          || t.slug.toLowerCase().includes(q)
          || t.email.toLowerCase().includes(q);
        if (!hit) return false;
      }
      if (tenantPlanFilter !== "all" && (t.planId ?? "starter") !== tenantPlanFilter) return false;
      if (tenantStatusFilter !== "all" && (t.subStatus ?? t.lifecycleStatus ?? "") !== tenantStatusFilter) return false;
      if (tenantProfileFilter !== "all" && (t.businessProfile ?? "catalog") !== tenantProfileFilter) return false;
      if (tenantDemoFilter !== "all" && getWorkspaceKind(t.productFlags) !== tenantDemoFilter) return false;
      return true;
    });
  }, [tenants, tenantSearch, tenantPlanFilter, tenantStatusFilter, tenantProfileFilter, tenantDemoFilter]);

  const topTenants = useMemo(() => {
    if (!stats) return [];
    return Object.entries(stats.byTenant)
      .map(([id, n]) => ({
        id,
        n,
        name: tenants.find((t) => t.id === id)?.name ?? id,
        slug: tenants.find((t) => t.id === id)?.slug,
      }))
      .sort((a, b) => b.n - a.n)
      .slice(0, 5);
  }, [stats, tenants]);

  const chartSeries = useMemo(
    () =>
      (stats?.ordersPerDay30 ?? []).map((d) => ({
        dia: d.date,
        pedidos: d.count,
      })),
    [stats],
  );

  const mrrUsd = useMemo(() => {
    if (!stats) return 0;
    return tenants.reduce((acc, t) => {
      if (t.subStatus !== "active") return acc;
      const plan = t.planId ?? "starter";
      return acc + (PLAN_PRICE_USD[plan] ?? 0);
    }, 0);
  }, [tenants, stats]);

  const mrrEstimate = dolarBlue ? Math.round(mrrUsd * dolarBlue.mid) : null;
  const tenantsWithOrder = useMemo(() => Object.keys(stats?.byTenant ?? {}).length, [stats]);
  const leadsConverted = useMemo(
    () => leads.filter((l) => l.status === "approved" || l.status === "converted").length,
    [leads],
  );
  const heatmapMax = useMemo(() => Math.max(...(stats?.ordersByHour ?? []).map((x) => x.count), 1), [stats]);

  const activationRate = useMemo(() => {
    if (!stats || stats.tenants === 0) return null;
    const withOrders = Object.keys(stats.byTenant).length;
    return Math.round((withOrders / stats.tenants) * 100);
  }, [stats]);

  if (loading && !stats) {
    return <PageLoader variant="screen" label="Cargando panel…" />;
  }

  return (
    <PlatformAdminShell
      tab={tab}
      onTabChange={setTabAndUrl}
      leadsPending={stats?.leadsPending}
      userEmail={userEmail}
      onLogout={onLogout}
      wideContent={tab === "planes" || tab === "nicho-nav" || tab === "nichos-modulos"}
    >
      {loading && (
        <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Actualizando…
        </div>
      )}

      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <AdminHubBack tab={tab} onTab={setTabAndUrl} />
          {!isAdminHubTab(tab) ? (
            <h1 className="text-xl font-bold text-foreground">{ADMIN_PAGE_TITLES[tab]}</h1>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/app/dashboard" className="gap-2">
              <LayoutDashboard className="h-4 w-4" /> App
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/marketing">Marketing</Link>
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTabAndUrl(v as AdminTabId)}>
        <TabsList className="mb-4 hidden">
          {ADMIN_TAB_IDS.map((id) => (
            <TabsTrigger key={id} value={id} />
          ))}
        </TabsList>

        <TabsContent value="hub" className="mt-0">
          <AdminDomainHub
            {...ADMIN_ROOT_HUB}
            onTab={setTabAndUrl}
            badges={{ negocio: (stats?.approvalsPending ?? 0) + (stats?.leadsPending ?? 0) || undefined }}
          />
        </TabsContent>

        <TabsContent value="dashboard" className="mt-0 space-y-6">
          {stats ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  title="Workspaces activos"
                  value={stats.activeTenants}
                  icon={Users}
                  variant="success"
                />
                <StatCard title="Pedidos del mes" value={stats.ordersTotal} icon={BarChart3} />
                <StatCard title="Usuarios totales" value={stats.users} icon={UserCircle} />
                <StatCard
                  title="Activación leads"
                  value={leads.length ? `${Math.round((leadsConverted / leads.length) * 100)}%` : "—"}
                  icon={Activity}
                />
              </div>
              <div className="grid gap-3 lg:grid-cols-3">
                <StatCard
                  title="MRR estimado (USD)"
                  value={`$${mrrUsd.toLocaleString("en-US")}`}
                  icon={BarChart3}
                  delta={
                    dolarBlue
                      ? `≈ $${mrrEstimate!.toLocaleString("es-AR")} ARS · Blue $${dolarBlue.mid.toFixed(0)}`
                      : "Cargando cotización blue…"
                  }
                />
                <StatCard title="Churn (30 días)" value={stats.churn30} icon={Shield} variant="warning" />
                <StatCard title="Workspaces con ≥1 pedido" value={tenantsWithOrder} icon={LayoutDashboard} />
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="glass-card rounded-xl p-4">
                  <p className="text-sm font-semibold text-foreground">Actividad hoy por workspace</p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Workspace</TableHead>
                        <TableHead className="text-right">Pedidos hoy</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenants
                        .map((tenant) => ({
                          tenant,
                          n: stats.ordersTodayByTenant[tenant.id] ?? 0,
                        }))
                        .filter((x) => x.n > 0)
                        .sort((a, b) => b.n - a.n)
                        .slice(0, 12)
                        .map(({ tenant, n }) => (
                          <TableRow key={tenant.id}>
                            <TableCell>{tenant.name}</TableCell>
                            <TableCell className="text-right">{n}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                  {tenants.every((t) => !(stats.ordersTodayByTenant[t.id] ?? 0)) ? (
                    <p className="mt-2 text-xs text-muted-foreground">Sin pedidos hoy.</p>
                  ) : null}
                </div>
                <div className="glass-card rounded-xl p-4">
                  <p className="text-sm font-semibold text-foreground">Workspaces por plan</p>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {Object.entries(stats.planBreakdown).map(([plan, n]) => (
                      <li key={plan}>
                        <span className="font-medium text-foreground">{planLabel(plan)}</span>: {n}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-sm font-semibold text-foreground">Top 5 por pedidos</p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {topTenants.map((t) => (
                      <li key={t.id} className="flex justify-between gap-2">
                        <span className="truncate">{t.name}</span>
                        <span className="text-muted-foreground">{t.n}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-sm font-semibold text-foreground">
                    Mapa de calor por hora (todos los pedidos)
                  </p>
                  <div className="mt-3 grid grid-cols-12 gap-1.5 sm:grid-cols-[repeat(24,minmax(0,1fr))]">
                    {(stats.ordersByHour ?? []).map(({ hour, count }) => {
                      const intensity = heatmapMax > 0 ? count / heatmapMax : 0;
                      return (
                        <div key={hour} className="flex flex-col items-center gap-1">
                          <div
                            title={`${count} pedido(s) a las ${String(hour).padStart(2, "0")}:xx`}
                            className="h-9 w-full min-w-0 rounded-sm bg-primary transition-opacity"
                            style={{ opacity: Math.max(0.12, intensity) }}
                          />
                          <span className="text-[9px] leading-none text-muted-foreground">{hour}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="glass-card rounded-xl p-4">
                <p className="text-sm font-semibold text-foreground">Pedidos acumulados por día (30 días)</p>
                <div className="mt-4 h-56 w-full min-w-0">
                  {chartSeries.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartSeries} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis
                          dataKey="dia"
                          tick={{ fontSize: 10 }}
                          tickFormatter={(v) => {
                            try {
                              const d = parseISO(String(v));
                              return isValid(d) ? format(d, "d MMM", { locale: es }) : String(v);
                            } catch {
                              return String(v);
                            }
                          }}
                          interval="preserveStartEnd"
                        />
                        <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={32} />
                        <Tooltip
                          labelFormatter={(v) => {
                            try {
                              const d = parseISO(String(v));
                              return isValid(d) ? format(d, "PPP", { locale: es }) : String(v);
                            } catch {
                              return String(v);
                            }
                          }}
                          formatter={(value: number) => [`${value} pedidos`, "Total"]}
                        />
                        <Line
                          type="monotone"
                          dataKey="pedidos"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-xs text-muted-foreground">Sin datos en el período.</p>
                  )}
                </div>
              </div>
              <div className="glass-card overflow-x-auto rounded-xl">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Pedidos totales</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(stats.byTenant).map(([id, n]) => (
                      <TableRow key={id}>
                        <TableCell>{tenants.find((t) => t.id === id)?.name ?? id}</TableCell>
                        <TableCell>{n}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">
                Embudo: {leads.length} leads → {leadsConverted} convertidos → {stats.activeTenants} activos →{" "}
                {tenantsWithOrder} con pedidos.
              </p>
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="negocio" className="mt-0">
          <AdminDomainHub
            {...ADMIN_HUBS.negocio}
            onTab={setTabAndUrl}
            badges={{ aprobaciones: stats?.approvalsPending || undefined, leads: stats?.leadsPending || undefined }}
          />
        </TabsContent>
        <TabsContent value="cuentas" className="mt-0">
          <AdminDomainHub {...ADMIN_HUBS.cuentas} onTab={setTabAndUrl} />
        </TabsContent>
        <TabsContent value="producto" className="mt-0">
          <AdminDomainHub {...ADMIN_HUBS.producto} onTab={setTabAndUrl} />
        </TabsContent>
        <TabsContent value="comunicaciones" className="mt-0">
          <AdminDomainHub {...ADMIN_HUBS.comunicaciones} onTab={setTabAndUrl} />
        </TabsContent>
        <TabsContent value="cobros" className="mt-0">
          <AdminDomainHub {...ADMIN_HUBS.cobros} onTab={setTabAndUrl} />
        </TabsContent>
        <TabsContent value="operaciones" className="mt-0">
          <AdminDomainHub {...ADMIN_HUBS.operaciones} onTab={setTabAndUrl} />
        </TabsContent>
        <TabsContent value="desarrollo" className="mt-0">
          <AdminDomainHub {...ADMIN_HUBS.desarrollo} onTab={setTabAndUrl} />
        </TabsContent>
        <TabsContent value="renata" className="mt-0">
          <AdminDomainHub {...ADMIN_HUBS.renata} onTab={setTabAndUrl} />
        </TabsContent>

        {/* SCRUM-225 — una card/tab por pasarela + self-service + routing + ops */}
        <TabsContent value="saas-mp" className="mt-0 space-y-6">
          <SaasMpBillingPanel />
        </TabsContent>
        <TabsContent value="cobros-payway" className="mt-0 space-y-6">
          <PaywayAdminPanel focus="payway" />
        </TabsContent>
        <TabsContent value="cobros-uala" className="mt-0 space-y-6">
          <PaywayAdminPanel focus="uala" />
        </TabsContent>
        <TabsContent value="cobros-transfer" className="mt-0 space-y-6">
          <SaasBillingConfigPanel section="transfer" />
        </TabsContent>
        <TabsContent value="cobros-self-service" className="mt-0 space-y-6">
          <SaasBillingConfigPanel section="self-service" />
        </TabsContent>
        <TabsContent value="cobros-routing" className="mt-0 space-y-6">
          <SaasBillingConfigPanel section="routing" />
        </TabsContent>
        <TabsContent value="cobros-ops" className="mt-0 space-y-6">
          <CobrosOpsPanel />
        </TabsContent>
        <TabsContent value="billing-admin" className="mt-0">
          <BillingAdminPanel />
        </TabsContent>
        <TabsContent value="feature-catalog" className="mt-0">
          <FeatureCatalogPanel />
        </TabsContent>
        <TabsContent value="packs" className="mt-0">
          <AdminPacksPanel />
        </TabsContent>
        <TabsContent value="help-analytics" className="mt-0">
          <HelpAnalyticsPanel />
        </TabsContent>
        <TabsContent value="usage-ops" className="mt-0">
          <PlatformUsageOpsPanel />
        </TabsContent>
        <TabsContent value="support-inbox" className="mt-0">
          <SupportInboxPanel />
        </TabsContent>
        <TabsContent value="platform-status" className="mt-0">
          <PlatformStatusPanel />
        </TabsContent>
        <TabsContent value="netlify-env" className="mt-0">
          <NetlifyEnvCenterPanel />
        </TabsContent>

        <TabsContent value="tenants" className="mt-0 space-y-6">
          <div className="space-y-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Input
                  className="h-9 max-w-sm bg-secondary/50 text-sm"
                  placeholder="Buscar workspace, slug o email…"
                  value={tenantSearch}
                  onChange={(e) => setTenantSearch(e.target.value)}
                  data-testid="workspace-search"
                />
                <p className="text-sm text-muted-foreground">{filteredTenants.length} workspaces</p>
                <Button size="sm" variant="outline" onClick={() => setCreateDemoLeadOpen(true)}>
                  Generar demo para lead
                </Button>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="workspace-filters">
                <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={tenantPlanFilter} onChange={(e) => setTenantPlanFilter(e.target.value)}>
                  <option value="all">Todos los planes</option>
                  <option value="starter">starter</option>
                  <option value="pro">pro</option>
                  <option value="business">business</option>
                  <option value="free_trial">free_trial</option>
                </select>
                <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={tenantStatusFilter} onChange={(e) => setTenantStatusFilter(e.target.value)}>
                  <option value="all">Todos los estados</option>
                  <option value="active">active</option>
                  <option value="trialing">trialing</option>
                  <option value="past_due">past_due</option>
                  <option value="canceled">canceled</option>
                </select>
                <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={tenantProfileFilter} onChange={(e) => setTenantProfileFilter(e.target.value)}>
                  <option value="all">Todos los perfiles</option>
                  {Object.entries(BUSINESS_PROFILE_LABELS).map(([id, label]) => (
                    <option key={id} value={id}>{label}</option>
                  ))}
                </select>
                <select className="h-9 rounded-md border border-input bg-background px-2 text-sm" value={tenantDemoFilter} onChange={(e) => setTenantDemoFilter(e.target.value)}>
                  <option value="all">Todas las categorías</option>
                  <option value="client">Solo clientes</option>
                  <option value="demo_seed">Solo demos con seed</option>
                  <option value="demo_lead">Solo demos por lead</option>
                </select>
              </div>
            </div>
            {/* Cards en mobile */}
            <div className="flex flex-col gap-3 md:hidden">
              {filteredTenants.map((t) => {
                const orders = stats?.byTenant[t.id] ?? 0;
                const today = stats?.ordersTodayByTenant[t.id] ?? 0;
                const workspaceKind = getWorkspaceKind(t.productFlags);
                const isDemo = workspaceKind !== "client";
                return (
                  <div key={t.id} className="glass-card rounded-xl border border-border/60 p-3 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{t.name}</p>
                          {isDemo && (
                            <Badge
                              variant="outline"
                              className="text-[10px] shrink-0"
                              title={
                                workspaceKind === "demo_lead" && t.onboardingDrafts?.demoLead
                                  ? `Contacto: ${t.onboardingDrafts.demoLead.contactName ?? "—"} · ${t.onboardingDrafts.demoLead.contactEmail ?? t.onboardingDrafts.demoLead.contactPhone ?? "sin datos"} · creado por ${t.onboardingDrafts.demoLead.createdBy}`
                                  : undefined
                              }
                            >
                              {WORKSPACE_KIND_LABELS[workspaceKind]}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{t.email}</p>
                        <a
                          href={t.subdomain ? `https://${t.subdomain}.${getTenantBaseDomain()}` : `/market/${t.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        >
                          {t.subdomain ? `${t.subdomain}.${getTenantBaseDomain()}` : `/market/${t.slug}`}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                      <WorkspaceActionsMenu
                        tenant={{
                          id: t.id,
                          name: t.name,
                          slug: t.slug,
                          lifecycleStatus: t.lifecycleStatus,
                          subscriptionStatus: t.subStatus,
                          isDemo,
                        }}
                        publicStoreHref={
                          t.subdomain ? `https://${t.subdomain}.${getTenantBaseDomain()}` : `/market/${t.slug}`
                        }
                        onImpersonate={() => impersonate(t.id)}
                        onProvisionSubdomain={() => void provisionTenantSubdomain(t.id, t.slug, t.subdomain)}
                        onCustomDomain={() => void setCustomDomain(t.id, t.customDomain)}
                        onLimitsOverride={() => setLimitsTenant({ id: t.id, name: t.name })}
                        onSetLifecycle={(status) => void setLifecycle(t.id, status)}
                        onSetSubscriptionStatus={(status) => void patchSubscription(t.id, status)}
                        onDelete={() => setDeleteTenantId(t.id)}
                        demoReset={
                          isDemo
                            ? {
                                label: "🧪 Reset pedidos (demo)",
                                onClick: () => {
                                  const slug = Object.entries(DEMO_TENANT_IDS).find(([, id]) => id === t.id)?.[0] as DemoSlug | undefined;
                                  if (slug) void runDemoReset(slug, t.id);
                                },
                              }
                            : undefined
                        }
                        demoReseed={
                          isDemo
                            ? {
                                label: "🌱 Reseed catálogo (demo)",
                                onClick: () => {
                                  const slug = Object.entries(DEMO_TENANT_IDS).find(([, id]) => id === t.id)?.[0] as DemoSlug | undefined;
                                  if (slug) void runDemoSeed(slug);
                                },
                              }
                            : undefined
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Plan</Label>
                        <Select value={t.planId ?? "starter"} onValueChange={(v) => void patchPlan(t.id, v)}>
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Plan" />
                          </SelectTrigger>
                          <SelectContent>
                            {planSelectOptions.map((p) => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                            {t.planId && !planSelectOptions.some((p) => p.id === t.planId) && (
                              <SelectItem value={t.planId}>{planLabel(t.planId)}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        {t.validUntil && <p className="text-[10px] text-muted-foreground">hasta {t.validUntil}</p>}
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Business Profile</Label>
                        <Select
                          value={(t.businessProfile as BusinessProfile) ?? "catalog"}
                          onValueChange={(v) => void patchBusinessProfile(t.id, v)}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`workspace-profile-${t.id}`}>
                            <SelectValue placeholder="Perfil" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(BUSINESS_PROFILE_LABELS).map(([id, label]) => (
                              <SelectItem key={id} value={id}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div>
                        {lifecycleBadge(t.lifecycleStatus)}
                        <p className="mt-0.5 text-[10px] text-muted-foreground">{t.subStatus ?? "—"}</p>
                      </div>
                      <p className="tabular-nums">
                        {orders} pedidos
                        {today > 0 && <span className="ml-1 text-[10px] text-emerald-600">+{today} hoy</span>}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="hidden glass-card overflow-x-auto rounded-xl md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Negocio</TableHead>
                    <TableHead>Tienda</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Business Profile</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Pedidos</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTenants.map((t) => {
                    const orders = stats?.byTenant[t.id] ?? 0;
                    const today = stats?.ordersTodayByTenant[t.id] ?? 0;
                    const workspaceKind = getWorkspaceKind(t.productFlags);
                    const isDemo = workspaceKind !== "client";
                    return (
                      <TableRow key={t.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{t.name}</p>
                            {isDemo && (
                              <Badge
                                variant="outline"
                                className="text-[10px]"
                                title={
                                  workspaceKind === "demo_lead" && t.onboardingDrafts?.demoLead
                                    ? `Contacto: ${t.onboardingDrafts.demoLead.contactName ?? "—"} · ${t.onboardingDrafts.demoLead.contactEmail ?? t.onboardingDrafts.demoLead.contactPhone ?? "sin datos"} · creado por ${t.onboardingDrafts.demoLead.createdBy}`
                                    : undefined
                                }
                              >
                                {WORKSPACE_KIND_LABELS[workspaceKind]}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{t.email}</p>
                        </TableCell>
                        <TableCell>
                          <a
                            href={
                              t.subdomain
                                ? `https://${t.subdomain}.${getTenantBaseDomain()}`
                                : `/market/${t.slug}`
                            }
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            {t.subdomain ? `${t.subdomain}.${getTenantBaseDomain()}` : `/market/${t.slug}`}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          <span
                            className="ml-1.5 inline-flex items-center gap-1 text-[10px] text-muted-foreground"
                            title="Slug inmutable (webhooks, market URL y QR)"
                          >
                            slug: {t.slug}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={t.planId ?? "starter"}
                            onValueChange={(v) => void patchPlan(t.id, v)}
                          >
                            <SelectTrigger className="h-8 min-w-[10.5rem] max-w-[14rem]">
                              <SelectValue placeholder="Plan" />
                            </SelectTrigger>
                            <SelectContent>
                              {planSelectOptions.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}
                                </SelectItem>
                              ))}
                              {/* Si el tenant tiene un planId fuera del catálogo, no perder la opción. */}
                              {t.planId &&
                                !planSelectOptions.some((p) => p.id === t.planId) && (
                                  <SelectItem value={t.planId}>{planLabel(t.planId)}</SelectItem>
                                )}
                            </SelectContent>
                          </Select>
                          {t.validUntil && (
                            <p className="mt-0.5 text-[10px] text-muted-foreground">hasta {t.validUntil}</p>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={(t.businessProfile as BusinessProfile) ?? "catalog"}
                            onValueChange={(v) => void patchBusinessProfile(t.id, v)}
                          >
                            <SelectTrigger className="h-8 min-w-[11rem] max-w-[16rem]" data-testid={`workspace-profile-${t.id}`}>
                              <SelectValue placeholder="Perfil" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(BUSINESS_PROFILE_LABELS).map(([id, label]) => (
                                <SelectItem key={id} value={id}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {lifecycleBadge(t.lifecycleStatus)}
                          <p className="mt-0.5 text-[10px] text-muted-foreground">{t.subStatus ?? "—"}</p>
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">
                          {orders}
                          {today > 0 && (
                            <span className="ml-1 text-[10px] text-emerald-600">+{today} hoy</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <WorkspaceActionsMenu
                            tenant={{
                              id: t.id,
                              name: t.name,
                              slug: t.slug,
                              lifecycleStatus: t.lifecycleStatus,
                              subscriptionStatus: t.subStatus,
                              isDemo,
                            }}
                            publicStoreHref={
                              t.subdomain
                                ? `https://${t.subdomain}.${getTenantBaseDomain()}`
                                : `/market/${t.slug}`
                            }
                            onImpersonate={() => impersonate(t.id)}
                            onProvisionSubdomain={() =>
                              void provisionTenantSubdomain(t.id, t.slug, t.subdomain)
                            }
                            onCustomDomain={() => void setCustomDomain(t.id, t.customDomain)}
                            onLimitsOverride={() => setLimitsTenant({ id: t.id, name: t.name })}
                            onSetLifecycle={(status) => void setLifecycle(t.id, status)}
                            onSetSubscriptionStatus={(status) => void patchSubscription(t.id, status)}
                            onDelete={() => setDeleteTenantId(t.id)}
                            demoReset={
                              isDemo
                                ? {
                                    label: "🧪 Reset pedidos (demo)",
                                    onClick: () => {
                                      const slug = Object.entries(DEMO_TENANT_IDS).find(
                                        ([, id]) => id === t.id,
                                      )?.[0] as DemoSlug | undefined;
                                      if (slug) void runDemoReset(slug, t.id);
                                    },
                                  }
                                : undefined
                            }
                            demoReseed={
                              isDemo
                                ? {
                                    label: "🌱 Reseed catálogo (demo)",
                                    onClick: () => {
                                      const slug = Object.entries(DEMO_TENANT_IDS).find(
                                        ([, id]) => id === t.id,
                                      )?.[0] as DemoSlug | undefined;
                                      if (slug) void runDemoSeed(slug);
                                    },
                                  }
                                : undefined
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 space-y-4" data-testid="sandbox-demos">
            <div>
              <p className="text-sm font-semibold text-violet-300">Sandbox</p>
              <p className="mt-1 text-xs text-muted-foreground max-w-2xl">
                Demos por nicho, clientes representativos, seeds y utilidades de reset. Re-seed actualiza
                catálogo/experiencia; reset borra pedidos de prueba.
              </p>
            </div>
            <div className="flex flex-wrap gap-1 border-b border-border/50">
              {([
                ["demos", "Workspace Demos"],
                ["clientes", "Workspace Clientes"],
                ["seeds", "Seeds"],
                ["utilities", "Utilities"],
              ] as const).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={cn(
                    "border-b-2 px-3 py-2 text-sm",
                    sandboxTab === id
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                  onClick={() => setSandboxTab(id)}
                >
                  {label}
                </button>
              ))}
            </div>

            {(sandboxTab === "demos" || sandboxTab === "seeds") && (
              <div className="grid gap-4 md:grid-cols-3">
                {DEMO_SLUGS.map((slug) => {
                  const tenantId = DEMO_TENANT_IDS[slug];
                  const tenant = tenants.find((t) => t.id === tenantId);
                  const busy = demoBusy?.startsWith(slug) || demoBusy === `${tenantId}-reset`;
                  return (
                    <Card key={slug} className="glass-card border-border/60">
                      <CardHeader>
                        <CardTitle className="text-base">{DEMO_LABELS[slug] ?? slug}</CardTitle>
                        <CardDescription>
                          <a href={`/market/${slug}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                            /market/{slug}
                          </a>
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        {tenant && (
                          <p className="text-xs text-muted-foreground">
                            {stats?.byTenant[tenantId] ?? 0} pedidos · plan {tenant.planId ?? "—"} · perfil{" "}
                            {tenant.businessProfile ?? "—"}
                          </p>
                        )}
                        <div className="flex flex-col gap-2">
                          <Button size="sm" disabled={busy} onClick={() => void runDemoSeed(slug)}>
                            {demoBusy === `${slug}-seed` ? (
                              <Loader2 className="h-4 w-4 animate-spin mr-1" />
                            ) : (
                              <RefreshCw className="h-4 w-4 mr-1" />
                            )}
                            Re-seed
                          </Button>
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => void runDemoReset(slug, tenantId)}>
                            Reset pedidos
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => impersonate(tenantId)}>
                            Abrir workspace
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {sandboxTab === "clientes" && (
              <div className="grid gap-4 md:grid-cols-3">
                {[
                  { slug: "pistacho", tenantId: "t-pistacho" },
                  { slug: "americanburger", tenantId: "t-burger-bosch" },
                  { slug: "el-cortejo", tenantId: "t-el-cortejo" },
                  { slug: "el-bodegon-de-san-lorenzo", tenantId: "t-bodegon-san-lorenzo" },
                  { slug: "petfy", tenantId: "t-petfy" },
                ].map((row) => {
                  const tenant = tenants.find((t) => t.id === row.tenantId);
                  return (
                    <Card key={row.slug} className="glass-card border-border/60">
                      <CardHeader>
                        <CardTitle className="text-base">{DEMO_LABELS[row.slug] ?? row.slug}</CardTitle>
                        <CardDescription>
                          <a href={`/market/${row.slug}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                            /market/{row.slug}
                          </a>
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          {tenant
                            ? `plan ${tenant.planId ?? "—"} · ${tenant.businessProfile ?? "catalog"}`
                            : "Tenant no listado (¿falta seed?)"}
                        </p>
                        {tenant ? (
                          <Button size="sm" variant="secondary" onClick={() => impersonate(row.tenantId)}>
                            Abrir workspace
                          </Button>
                        ) : null}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {sandboxTab === "utilities" && (
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Resets disponibles por demo (desde cada card en Demos/Seeds):</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Re-seed — catálogo, branding, experience, analytics demo</li>
                  <li>Reset pedidos — limpia órdenes de prueba</li>
                  <li>Reset clientes / carrito / promociones — vía seed completo (Re-seed)</li>
                </ul>
                <p className="text-xs">Catalog Imports: usar Import CSV en el workspace impersonado.</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="leads" className="mt-0">
          <LeadsPanel onViewDetail={setLeadDetailId} />
        </TabsContent>

        <TabsContent value="users" className="mt-0 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-secondary/15 px-3 py-2">
            <p className="max-w-2xl text-xs text-muted-foreground">
              <span className="font-medium text-foreground">Gestión de cuentas de tenants:</span> creá owner/staff con
              contraseña temporal y workspace. Staff Structura (admin/soporte/lectura) se gestiona en
              «Staff plataforma».
            </p>
            <Button
              type="button"
              size="sm"
              className="shrink-0 gap-1"
              onClick={() => {
                setNuEmail("");
                setNuName("");
                setNuRole("owner");
                setNuTenantId(tenants[0]?.id ?? "");
                setNewUserOpen(true);
              }}
            >
              <UserPlus className="h-4 w-4" />
              Nuevo usuario
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              placeholder="Buscar por email o nombre…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="h-9 w-56 bg-secondary/50 text-xs"
            />
            <span className="text-xs text-muted-foreground">Workspace</span>
            <Select value={userTenantFilter} onValueChange={setUserTenantFilter}>
              <SelectTrigger className="h-9 w-[200px] bg-secondary/50 text-xs">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">Rol</span>
            <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
              <SelectTrigger className="h-9 w-[160px] bg-secondary/50 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="super_admin">super_admin</SelectItem>
                <SelectItem value="support">support</SelectItem>
                <SelectItem value="viewer">viewer</SelectItem>
                <SelectItem value="owner">owner</SelectItem>
                <SelectItem value="staff">staff</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Cards en mobile */}
          <div className="flex flex-col gap-2 md:hidden">
            {users.map((u) => {
              const tn = u.tenantId ? tenants.find((t) => t.id === u.tenantId) : undefined;
              const ordersCount = u.tenantId ? (stats?.byTenant[u.tenantId] ?? 0) : 0;
              return (
                <div key={u.id} className="glass-card rounded-xl border border-border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    </div>
                    {u.role === "super_admin" ? (
                      <Badge variant="outline" className="shrink-0">{u.role}</Badge>
                    ) : (
                      <Select
                        value={u.role}
                        onValueChange={(v) => {
                          void (async () => {
                            const r = await patchAdminUser({ userId: u.id, role: v as "owner" | "staff" });
                            if ("error" in r) toast.error(r.error);
                            else {
                              toast.success("Rol actualizado");
                              void loadAll();
                            }
                          })();
                        }}
                      >
                        <SelectTrigger className="h-8 w-[100px] shrink-0 bg-secondary/50 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="owner">owner</SelectItem>
                          <SelectItem value="staff">staff</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {u.tenantId && tn ? (
                      <p>Workspace: <span className="font-medium text-foreground">{tn.name}</span></p>
                    ) : (
                      <Badge variant="secondary">Sin workspace</Badge>
                    )}
                    <p className="mt-0.5">
                      Registro:{" "}
                      {u.createdAt && isValid(parseISO(u.createdAt))
                        ? format(parseISO(u.createdAt), "dd/MM/yyyy", { locale: es })
                        : "—"}
                      {u.tenantId ? ` · ${ordersCount} pedidos` : ""}
                    </p>
                    <p className="mt-0.5">
                      Último acceso:{" "}
                      {u.lastAccessAt && isValid(parseISO(u.lastAccessAt))
                        ? format(parseISO(u.lastAccessAt), "dd/MM/yyyy HH:mm", { locale: es })
                        : "—"}
                      {" · "}Activo: {u.active === false ? "No" : "Sí"}
                    </p>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Button size="sm" variant="secondary" className="text-xs" onClick={() => openUserEditor(u)}>
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      disabled={u.role === "super_admin"}
                      onClick={async () => {
                        try {
                          const res = await apiSendJson<{ tempPassword: string }>(
                            "/api/admin/set-password",
                            "POST",
                            { userId: u.id },
                          );
                          setPwUser(u);
                          setPwTemp(res.tempPassword);
                        } catch (e) {
                          toast.error(e instanceof Error ? e.message : "Error");
                        }
                      }}
                    >
                      Contraseña temporal
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs"
                      onClick={() => {
                        void (async () => {
                          const on = u.active !== false;
                          const r = await patchAdminUser({ userId: u.id, active: !on });
                          if ("error" in r) toast.error(r.error);
                          else {
                            toast.success(on ? "Desactivado" : "Activado");
                            void loadAll();
                          }
                        })();
                      }}
                    >
                      {u.active === false ? "Activar" : "Desactivar"}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="text-xs"
                      disabled={u.role === "super_admin"}
                      onClick={async () => {
                        if (!(await confirm({
                          title: `¿Eliminar usuario ${u.email}?`,
                          description: "Esta acción es irreversible.",
                          destructive: true,
                          confirmLabel: "Eliminar",
                        }))) return;
                        try {
                          await fetch(`/api/admin/users?id=${encodeURIComponent(u.id)}`, {
                            method: "DELETE",
                            credentials: "include",
                            headers: authHeaders(),
                          });
                          toast.success("Usuario eliminado");
                          void loadAll();
                        } catch {
                          toast.error("No se pudo eliminar");
                        }
                      }}
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden glass-card overflow-x-auto rounded-xl md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Workspace</TableHead>
                  <TableHead>Último acceso</TableHead>
                  <TableHead>Activo</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const tn = u.tenantId ? tenants.find((t) => t.id === u.tenantId) : undefined;
                  const ordersCount = u.tenantId ? (stats?.byTenant[u.tenantId] ?? 0) : 0;
                  return (
                    <TableRow key={u.id}>
                      <TableCell>
                        <div className="font-medium">{u.name}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          Registro:{" "}
                          {u.createdAt && isValid(parseISO(u.createdAt))
                            ? format(parseISO(u.createdAt), "dd/MM/yyyy", { locale: es })
                            : "—"}
                          {u.tenantId ? ` · ${ordersCount} pedidos en workspace` : ""}
                        </div>
                      </TableCell>
                      <TableCell>
                        {u.role === "super_admin" ? (
                          u.role
                        ) : (
                          <Select
                            value={u.role}
                            onValueChange={(v) => {
                              void (async () => {
                                const r = await patchAdminUser({
                                  userId: u.id,
                                  role: v as "owner" | "staff",
                                });
                                if ("error" in r) toast.error(r.error);
                                else {
                                  toast.success("Rol actualizado");
                                  void loadAll();
                                }
                              })();
                            }}
                          >
                            <SelectTrigger className="h-8 w-[110px] bg-secondary/50 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="owner">owner</SelectItem>
                              <SelectItem value="staff">staff</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.tenantId && tn ? (
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium">{tn.name}</span>
                            <span className="text-xs font-mono text-muted-foreground">
                              {u.tenantId.slice(0, 8)}…
                            </span>
                          </div>
                        ) : (
                          <Badge variant="secondary">Sin workspace</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {u.lastAccessAt && isValid(parseISO(u.lastAccessAt))
                          ? format(parseISO(u.lastAccessAt), "dd/MM/yyyy HH:mm", { locale: es })
                          : "—"}
                      </TableCell>
                      <TableCell className="text-xs">{u.active === false ? "No" : "Sí"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            className="text-xs"
                            onClick={() => openUserEditor(u)}
                          >
                            Editar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            disabled={u.role === "super_admin"}
                            onClick={async () => {
                              try {
                                const res = await apiSendJson<{ tempPassword: string }>(
                                  "/api/admin/set-password",
                                  "POST",
                                  { userId: u.id },
                                );
                                setPwUser(u);
                                setPwTemp(res.tempPassword);
                              } catch (e) {
                                toast.error(e instanceof Error ? e.message : "Error");
                              }
                            }}
                          >
                            Contraseña temporal
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => {
                              void (async () => {
                                const on = u.active !== false;
                                const r = await patchAdminUser({ userId: u.id, active: !on });
                                if ("error" in r) toast.error(r.error);
                                else {
                                  toast.success(on ? "Desactivado" : "Activado");
                                  void loadAll();
                                }
                              })();
                            }}
                          >
                            {u.active === false ? "Activar" : "Desactivar"}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="text-xs"
                            disabled={u.role === "super_admin"}
                            onClick={async () => {
                              if (!(await confirm({
                                title: `¿Eliminar usuario ${u.email}?`,
                                description: "Esta acción es irreversible.",
                                destructive: true,
                                confirmLabel: "Eliminar",
                              }))) return;
                              try {
                                await fetch(`/api/admin/users?id=${encodeURIComponent(u.id)}`, {
                                  method: "DELETE",
                                  credentials: "include",
                                  headers: authHeaders(),
                                });
                                toast.success("Usuario eliminado");
                                void loadAll();
                              } catch {
                                toast.error("No se pudo eliminar");
                              }
                            }}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {usersHasMore && (
            <div className="flex justify-center pt-2">
              <Button variant="outline" size="sm" onClick={() => void loadMoreUsers()} disabled={usersLoadingMore}>
                {usersLoadingMore ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                Cargar más
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="platform-staff" className="mt-0 space-y-3">
          <PlatformStaffPanel />
        </TabsContent>

        <TabsContent value="invitaciones" className="mt-0 space-y-6">
          <div className="glass-card rounded-xl border border-border p-5">
            <h3 className="mb-4 flex items-center gap-2 font-semibold">
              <UserPlus className="size-4 text-teal-400" /> Enviar invitación de prueba
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Email del destinatario</Label>
                <Input
                  value={invEmail}
                  onChange={(e) => setInvEmail(e.target.value)}
                  placeholder="cliente@ejemplo.com"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Nombre del negocio</Label>
                <Input
                  value={invBusiness}
                  onChange={(e) => setInvBusiness(e.target.value)}
                  placeholder="Carnicería XYZ"
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Días de trial</Label>
                <Input
                  type="number"
                  min={7}
                  max={60}
                  value={invDays}
                  onChange={(e) => setInvDays(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Nota personal (opcional)</Label>
                <Input
                  value={invNotes}
                  onChange={(e) => setInvNotes(e.target.value)}
                  placeholder="Te invito a probar Market…"
                  className="h-8 text-sm"
                />
              </div>
            </div>
            {inviteSent && (
              <div className="mt-3 space-y-2 rounded-lg border border-emerald-700/40 bg-emerald-950/40 p-3">
                <span className="text-xs font-medium text-emerald-300">
                  {inviteEmailSent
                    ? "✓ Invitación creada y email enviado."
                    : "✓ Invitación creada. Email no enviado — compartí el link de aceptación."}
                </span>
                <code className="block break-all text-xs text-teal-400">{inviteSent}</code>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      void navigator.clipboard.writeText(inviteSent);
                      toast.success("Link copiado");
                    }}
                  >
                    Copiar link
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs bg-green-600/20 text-green-300 hover:bg-green-600/30"
                    onClick={() => {
                      const msg = `¡Hola! Te invitamos a probar Structura Market 🎉\n\nActivá tu cuenta desde:\n${inviteSent}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
                    }}
                  >
                    Compartir por WhatsApp
                  </Button>
                </div>
              </div>
            )}
            <Button
              size="sm"
              className="mt-4 bg-teal-600 hover:bg-teal-500"
              disabled={inviteSending || !invEmail.trim() || !invBusiness.trim()}
              onClick={() => void sendInvitation()}
            >
              {inviteSending ? "Creando…" : "Crear invitación →"}
            </Button>
          </div>
          {/* Cards en mobile */}
          <div className="flex flex-col gap-2 md:hidden">
            {invitations.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">Sin invitaciones creadas todavía</p>
            )}
            {invitations.map((inv) => {
              const status = inv.status ?? (inv.accepted_at ? "accepted" : "pending");
              const expired = inv.expires_at && new Date(inv.expires_at) < new Date() && status === "pending";
              const displayStatus = expired ? "expired" : status;
              return (
                <div key={inv.id} className="glass-card rounded-xl border border-border p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{inv.email}</p>
                      <p className="text-xs text-muted-foreground truncate">{inv.business_name}</p>
                    </div>
                    <Badge
                      variant={displayStatus === "accepted" ? "default" : displayStatus === "expired" ? "destructive" : "secondary"}
                      className="shrink-0"
                    >
                      {displayStatus === "accepted" ? "Aceptada" : displayStatus === "expired" ? "Expirada" : "Pendiente"}
                    </Badge>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Trial: {inv.trial_days}d · Vence: {inv.expires_at ? formatDateTimeEs(inv.expires_at, "dd/MM/yyyy") : "—"}
                  </p>
                  {inv.token && displayStatus === "pending" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-2 text-xs text-teal-400"
                      onClick={() => {
                        const url = `${window.location.origin}/invitation/${inv.token}`;
                        void navigator.clipboard.writeText(url);
                        toast.success("Link copiado");
                      }}
                    >
                      Copiar link
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          <div className="hidden glass-card overflow-x-auto rounded-xl md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Destinatario</TableHead>
                  <TableHead>Trial</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Vence</TableHead>
                  <TableHead className="text-right">Link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                      Sin invitaciones creadas todavía
                    </TableCell>
                  </TableRow>
                )}
                {invitations.map((inv) => {
                  const status = inv.status ?? (inv.accepted_at ? "accepted" : "pending");
                  const expired =
                    inv.expires_at && new Date(inv.expires_at) < new Date() && status === "pending";
                  const displayStatus = expired ? "expired" : status;
                  return (
                    <TableRow key={inv.id}>
                      <TableCell>
                        <div className="text-sm font-medium">{inv.email}</div>
                        <div className="text-xs text-muted-foreground">{inv.business_name}</div>
                      </TableCell>
                      <TableCell className="text-sm">{inv.trial_days}d</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            displayStatus === "accepted"
                              ? "default"
                              : displayStatus === "expired"
                                ? "destructive"
                                : "secondary"
                          }
                        >
                          {displayStatus === "accepted"
                            ? "Aceptada"
                            : displayStatus === "expired"
                              ? "Expirada"
                              : "Pendiente"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {inv.expires_at ? formatDateTimeEs(inv.expires_at, "dd/MM/yyyy") : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        {inv.token && displayStatus === "pending" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-xs text-teal-400"
                            onClick={() => {
                              const url = `${window.location.origin}/invitation/${inv.token}`;
                              void navigator.clipboard.writeText(url);
                              toast.success("Link copiado");
                            }}
                          >
                            Copiar link
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="flags" className="mt-0">
          <AdminFlagsPanel tenants={tenants.map((t) => ({ id: t.id, name: t.name, slug: t.slug }))} />
        </TabsContent>

        <TabsContent value="analytics-metrics" className="mt-0">
          <AdminAnalyticsMetricsPanel />
        </TabsContent>
        <TabsContent value="cron-registry" className="mt-0">
          <AdminCronRegistryPanel />
        </TabsContent>

        <TabsContent value="planes" className="mt-0">
          <AdminPlansCatalogPanel />
        </TabsContent>

        <TabsContent value="permisos" className="mt-0 space-y-6">
          <p className="text-xs text-muted-foreground">
            Permisos del rol <code>staff</code> en el workspace. Miembros en <strong>Usuarios</strong>;
            plantillas en <strong>Roles</strong>.
          </p>
          <div className="glass-card overflow-x-auto rounded-xl">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Permiso</TableHead>
                  <TableHead className="w-28 text-center">owner</TableHead>
                  <TableHead className="w-36 text-center">staff</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {STAFF_PERMISSION_ROWS.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell className="text-sm">{row.label}</TableCell>
                    <TableCell className="text-center text-sm font-medium text-green-600 dark:text-green-500">
                      ✓
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={staffPermState[row.key]}
                        onCheckedChange={(v) => setStaffPermState((prev) => ({ ...prev, [row.key]: v }))}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" onClick={() => void saveStaffPermissions()}>
              Guardar permisos
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setStaffPermState({ ...STAFF_PERMISSION_DEFAULTS });
                toast.success("Permisos restaurados a defaults (recordá guardar)");
              }}
            >
              Restaurar defaults
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Esta plantilla define los permisos base del rol <code>staff</code> para nuevos miembros.
          </p>
        </TabsContent>

        <TabsContent value="roles" className="mt-0">
          <AdminRolesPanel tenants={tenants.map((t) => ({ id: t.id, name: t.name }))} />
        </TabsContent>

        <TabsContent value="platform" className="mt-0 space-y-6">
          <AdminPlatformEmailPanel
            userEmail={userEmail}
            tenants={tenants.map((t) => ({ id: t.id, name: t.name }))}
          />
        </TabsContent>

        <TabsContent value="push" className="mt-0">
          <PushTestingPanel />
        </TabsContent>

        <TabsContent value="releases" className="mt-0">
          <AdminReleasesPanel />
        </TabsContent>

        <TabsContent value="aprobaciones" className="mt-0">
          <AdminApprovalsPanel />
        </TabsContent>

        <TabsContent value="onboarding" className="mt-0">
          <OnboardingSessionsPanel />
        </TabsContent>

        <TabsContent value="compliance" className="mt-0">
          <PlatformComplianceRequestsPanel />
        </TabsContent>

        <TabsContent value="messaging" className="mt-0">
          <MessagingAdminPanel />
        </TabsContent>

        <TabsContent value="email-templates" className="mt-0">
          <AdminEmailTemplatesPanel userEmail={userEmail} />
        </TabsContent>
        <TabsContent value="email-gates" className="mt-0">
          <AdminEmailGatesPanel />
        </TabsContent>

        <TabsContent value="nicho-nav" className="mt-0">
          <ProfileNavMatrixPanel />
        </TabsContent>

        <TabsContent value="nichos-modulos" className="mt-0">
          <CapabilityNichesMatrixPanel />
        </TabsContent>
      </Tabs>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
            <DialogDescription>
              Los cambios se guardan en la base de datos. El email debe ser único en toda la plataforma.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Nombre</Label>
              <Input
                className="bg-secondary/50"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input
                type="email"
                className="bg-secondary/50"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
              />
            </div>
            {editUser?.role !== "super_admin" ? (
              <div className="space-y-1.5">
                <Label className="text-xs">Workspace</Label>
                <Select value={editWorkspaceSelectValue} onValueChange={setEditWorkspaceId}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_TENANT_SELECT}>Sin workspace</SelectItem>
                    {editWorkspaceOptions.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setEditUser(null)}>
              Cancelar
            </Button>
            <Button type="button" onClick={() => void saveEditedUser()}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newUserOpen} onOpenChange={setNewUserOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Email</Label>
              <Input
                className="h-9"
                value={nuEmail}
                onChange={(e) => setNuEmail(e.target.value)}
                type="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nombre</Label>
              <Input className="h-9" value={nuName} onChange={(e) => setNuName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Workspace</Label>
              <Select value={nuTenantId} onValueChange={setNuTenantId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Elegí workspace" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Rol</Label>
              <Select value={nuRole} onValueChange={(v) => setNuRole(v as "owner" | "staff")}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">staff</SelectItem>
                  <SelectItem value="owner">owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewUserOpen(false)}>
              Cancelar
            </Button>
            <Button disabled={nuSaving} onClick={() => void createAdminUser()}>
              {nuSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTemp} onOpenChange={(o) => !o && (setPwTemp(null), setPwUser(null))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Contraseña temporal</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pwUser?.email ? (
              <>
                Para <strong>{pwUser.email}</strong>. Copiala ahora; no se vuelve a mostrar.
              </>
            ) : (
              "Copiala ahora; no se vuelve a mostrar."
            )}
          </p>
          <code className="block rounded-lg bg-secondary px-3 py-2 text-sm font-mono">{pwTemp}</code>
          <DialogFooter>
            <Button
              onClick={() => {
                if (pwTemp) void navigator.clipboard.writeText(pwTemp);
                toast.success("Copiada");
              }}
            >
              Copiar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteTenantId}
        onOpenChange={(o) => {
          if (!o) {
            setDeleteTenantId(null);
            setDeleteConfirmSlug("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar workspace?</AlertDialogTitle>
            <AlertDialogDescription>
              Acción irreversible: se borran DB (tenant-scoped), DNS y objetos R2 del prefijo del tenant.
              {(() => {
                const t = tenants.find((x) => x.id === deleteTenantId);
                return t ? (
                  <>
                    {" "}
                    Objetivo: <strong>{t.name}</strong> (<code>{t.slug}</code>).
                  </>
                ) : null;
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="delete-confirm-slug" className="text-xs">
              Escribí el slug exacto para confirmar
            </Label>
            <Input
              id="delete-confirm-slug"
              value={deleteConfirmSlug}
              onChange={(e) => setDeleteConfirmSlug(e.target.value)}
              placeholder={tenants.find((x) => x.id === deleteTenantId)?.slug ?? "slug"}
              autoComplete="off"
              className="h-10 font-mono text-sm"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={
                deleteBusy ||
                !deleteTenantId ||
                deleteConfirmSlug.trim() !== (tenants.find((x) => x.id === deleteTenantId)?.slug ?? "")
              }
              onClick={(e) => {
                e.preventDefault();
                if (deleteTenantId) void deleteTenant(deleteTenantId);
              }}
            >
              {deleteBusy ? "Eliminando…" : "Eliminar definitivamente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CreateDemoLeadDialog
        open={createDemoLeadOpen}
        onOpenChange={setCreateDemoLeadOpen}
        onCreated={() => void loadAll()}
      />

      <TenantLimitsOverrideDialog
        tenantId={limitsTenant?.id ?? null}
        tenantName={limitsTenant?.name}
        open={limitsTenant != null}
        onOpenChange={(open) => {
          if (!open) setLimitsTenant(null);
        }}
        onSaved={() => void loadAll()}
      />

      <LeadDetailDialog
        leadId={leadDetailId}
        open={leadDetailId != null}
        onOpenChange={(open) => {
          if (!open) setLeadDetailId(null);
        }}
      />
    </PlatformAdminShell>
  );
}
